import React, { useRef, useState } from "react";
import {
  Checkbox,
  Col,
  ColorPicker,
  Form,
  Input,
  Modal,
  Row,
  message,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import ColorOptions, { toHexColor } from "./ColorOptions";
import { PRESET_BADGES } from "./BadgeLogoPicker";

export const QUICK_CHAT_LAYOUTS = [
  { id: "image-right", label: "Image right" },
  { id: "image-left", label: "Image left" },
  { id: "text-only", label: "Text only" },
  { id: "custom-image", label: "Custom image" },
];

const LayoutIcon = ({ type }) => {
  const bar = <span className="quickChatLayoutIconBar" />;
  const square = <span className="quickChatLayoutIconSquare" />;

  if (type === "image-right") {
    return (
      <span className="quickChatLayoutIcon">
        {bar}
        {square}
      </span>
    );
  }

  if (type === "image-left") {
    return (
      <span className="quickChatLayoutIcon">
        {square}
        {bar}
      </span>
    );
  }

  if (type === "text-only") {
    return (
      <span className="quickChatLayoutIcon quickChatLayoutIcon--textOnly">
        {bar}
      </span>
    );
  }

  return (
    <span className="quickChatLayoutIcon quickChatLayoutIcon--customImage">
      <span className="quickChatLayoutIconSquare quickChatLayoutIconSquare--large" />
    </span>
  );
};

const QuickChatTab = ({
  layout,
  setLayout,
  message,
  setMessage,
  bgColor,
  setBgColor,
  sameAsTheme,
  setSameAsTheme,
  themeColor,
  selectedLogo,
  onSelectPreset,
  onCustomUpload,
  customPreview,
  onRemoveImage,
  bgcolorBadge,
  onChange,
}) => {
  const fileInputRef = useRef(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const effectiveBgColor = sameAsTheme ? themeColor || bgColor : bgColor;
  const currentHex = toHexColor(effectiveBgColor);
  const previewSrc = customPreview || null;
  const canRemove = Boolean(previewSrc);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      message.error("Please upload an image file");
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      message.error("File size exceeds 1MB limit");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      onCustomUpload(reader.result, file);
    };
    reader.readAsDataURL(file);
  };

  const handleHexChange = (event) => {
    const value = event.target.value;
    if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
      setBgColor(value.toUpperCase());
      setSameAsTheme(false);
    }
  };

  const handleColorChange = (selectedColor, hex) => {
    if (typeof selectedColor === "string") {
      setBgColor(selectedColor);
    } else {
      setBgColor(
        hex || selectedColor?.toHexString?.() || selectedColor?.toRgbString?.(),
      );
    }
    setSameAsTheme(false);
  };

  const handleGallerySelect = (logoId, src) => {
    onSelectPreset(logoId, src);
    setGalleryOpen(false);
  };

  return (
    <Row>
      <Col xs={24} lg={16}>
        <Form
          layout="vertical"
          className="quickChatTab"
          colon={false}
          requiredMark={false}
        >
          <Form.Item
            label="Layout"
            className="quickChatTabLayoutField"
            style={{ marginBottom: 20 }}
          >
            <div className="quickChatLayoutGrid">
              {QUICK_CHAT_LAYOUTS.map((item) => {
                const isSelected = layout === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`quickChatLayoutOption${isSelected ? " selected" : ""}`}
                    onClick={() => setLayout(item.id)}
                    aria-pressed={isSelected}
                  >
                    <LayoutIcon type={item.id} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </Form.Item>

          <Form.Item label="Background color" style={{ marginBottom: 20 }}>
            <div className="quickChatColorRow">
              <div className="quickChatColorPickerWrap">
                <ColorOptions
                  value={currentHex || undefined}
                  onChange={handleColorChange}
                />
              </div>
              <Checkbox
                checked={sameAsTheme}
                onChange={(e) => setSameAsTheme(e.target.checked)}
              >
                Same as theme color
              </Checkbox>
            </div>
          </Form.Item>

          {layout !== "custom-image" && (
            <Form.Item label="Message" style={{ marginBottom: 20 }}>
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={25}
                placeholder="Chat with us"
              />
            </Form.Item>
          )}

          {layout !== "text-only" && (
            <Form.Item label="Image" style={{ marginBottom: 20 }}>
              <div className="quickChatImageRow">
                <div
                  className="quickChatImagePreview"
                  style={{ backgroundColor: bgcolorBadge || "#2e3192" }}
                >
                  {previewSrc ? (
                    <img src={previewSrc} alt="Quick chat badge" />
                  ) : (
                    <span className="quickChatImagePlaceholder" />
                  )}
                </div>
                <div className="quickChatImageActions">
                  <button
                    type="button"
                    className="quickChatImageBtn"
                    onClick={() => setGalleryOpen(true)}
                  >
                    Choose from gallery
                  </button>
                  <button
                    type="button"
                    className="quickChatImageBtn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload image
                  </button>
                  <button
                    type="button"
                    className="quickChatImageBtn quickChatImageBtn--muted"
                    disabled={!canRemove}
                    onClick={onRemoveImage}
                  >
                    Remove
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                </div>
              </div>
              <p className="quickChatImageHint">
                Maximum size 1 MB. Recommended dimensions 100x100px. To support
                retina displays, recommended dimensions are 200x200px.
              </p>
            </Form.Item>
          )}

          <Modal
            title="Choose from gallery"
            open={galleryOpen}
            onCancel={() => setGalleryOpen(false)}
            footer={null}
            width={520}
            destroyOnClose
          >
            <div className="quickChatGalleryGrid">
              {PRESET_BADGES.map((badge) => {
                const isSelected = selectedLogo === badge.id;
                return (
                  <button
                    key={badge.id}
                    type="button"
                    className={`quickChatGalleryItem${isSelected ? " selected" : ""}`}
                    onClick={() => handleGallerySelect(badge.id, badge.src)}
                    aria-pressed={isSelected}
                  >
                    <div
                      className="quickChatGalleryItemImage"
                      style={{ backgroundColor: bgcolorBadge || "#2e3192" }}
                    >
                      <img src={badge.src} alt="" />
                    </div>
                    {isSelected && (
                      <span className="badgeLogoPickerCheck" aria-hidden="true">
                        <CheckOutlined />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Modal>
        </Form>
      </Col>
    </Row>
  );
};

export default QuickChatTab;
